/**
 * Axios API client with Clerk authentication
 * Provides a hook to get an authenticated axios instance
 */
import axios from "axios";
import { useAuth } from "@clerk/clerk-react";
import { useRef, useEffect } from "react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Hook to get an authenticated axios instance
 * Usage: const api = useApi();
 *        const response = await api.get('/api/endpoint');
 */
export function useApi() {
  const { getToken, isLoaded } = useAuth();
  const apiRef = useRef(null);

  // Create axios instance once
  if (!apiRef.current) {
    apiRef.current = axios.create({
      baseURL: API_URL,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Response interceptor for error handling
    apiRef.current.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          console.warn("Authentication required");
        }
        return Promise.reject(error);
      }
    );
  }

  // Update the request interceptor when auth state changes
  useEffect(() => {
    const interceptorId = apiRef.current.interceptors.request.use(
      async (config) => {
        if (isLoaded) {
          try {
            const token = await getToken();
            if (token) {
              config.headers.Authorization = `Bearer ${token}`;
            }
          } catch (error) {
            // Silently fail - user might not be logged in
            console.debug("No auth token available");
          }
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Cleanup previous interceptor
    return () => {
      apiRef.current.interceptors.request.eject(interceptorId);
    };
  }, [getToken, isLoaded]);

  return apiRef.current;
}

// For backward compatibility - plain axios instance without auth
// Use this only for public endpoints
export const publicApi = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

export { API_URL };
